<script lang="ts" context="module">
    export const headerHeight = writable(0)
    export const headerAnimationOpen = writable(false)

    // This allows us to wait until the animation is ready, then load the page, otherwise the animation is jumpy
    export const headerAnimationsReady = derived(
        [headerAnimationOpen, topNavigatorOpen],
        ([animation, navigator]) => {
            if (!navigator) return true
            return animation
        },
    )
</script>

<script lang="ts">
    import { selectedTab, tabs, tabsReady } from "@store/pageTracker"

    import Icon from "@components/material/Icon.svelte"
    import IconDiv from "@components/material/IconDiv.svelte"
    import { navigatorOpen, toggle } from "@components/navigator/Navigator.svelte"
    // import { tabsListPopup } from "@components/tabs/TabsListPopup.svelte";
    import TopBarTabs from "@components/TopBar/TopBarTabs.svelte"
    import { slide } from "@utils/material/customAnimations"
    import { sideMenuOpen } from "@components/navigator/SideMenu.svelte"
    import TopNavigator from "@components/topNavigator/TopNavigator.svelte"
    import { acim } from "@utils/acim"
    import { cssObserver, updateHtmlClass } from "@utils/directives/useCSSObserver"
    import { expanded } from "@utils/material/expandable"
    import {
        audioElement,
        openAudio,
        audioButtonVisible,
        checking,
        buffering,
        audioItem,
    } from "@utils/audiobook/audiobook"
    import TopNavigation, { topNavigatorOpen } from "./TopNavigation.svelte"
    import { clickEnter } from "@utils/directives/accessibility"
    import { isActive } from "@utils/helpers/is-active"
    import { page } from "$app/stores"
    import { derived, writable } from "svelte/store"
    import EditTab from "@components/popup/templates/EditTab.svelte"

    // Tabs buttons should only be visible on the book page
    let tabsButtonVisible = false
    $: tabsButtonVisible = !!($tabsReady && $page?.params.notation)

    // Default tabs to be open
    let tabsOpen = true

    let bookPage: boolean
    $: bookPage = $isActive("./book/")

    let amountOfTabs: number
    $: amountOfTabs = $tabs.size

    /**
     * Toggles the navigator
     * If no tabs, creates a new tab
     */
    function openPages() {
        if (amountOfTabs) {
            tabsOpen = !tabsOpen
            // $tabsListPopup = "go"
            // open tabs thing
        } else {
            addTabDialogueOpen = true
        }
    }

    let addTabDialogueOpen = false

    $: $topNavigatorOpen = tabsButtonVisible && tabsOpen && amountOfTabs > 0
    $: updateHtmlClass("topNavigatorOpen", $topNavigatorOpen) // save to html class for use in padding-top of text-container
</script>

{#if addTabDialogueOpen}
    <EditTab tab={null} on:exit={() => (addTabDialogueOpen = false)} />
{/if}

<!-- svelte-ignore component-name-lowercase -->
{#if !$expanded}
    <header
        class="topbar-header"
        transition:slide|global
        use:cssObserver={{
            store: headerHeight,
            fn: (e) => e.contentRect.height,
        }}
    >
        <div class="headerWrapper">
            <div class="flex items-center">
                <div
                    role="button"
                    tabindex="0"
                    aria-label="toggle table of contents & quick search"
                    aria-controls="navigator"
                    aria-expanded={$navigatorOpen}
                    on:keydown={clickEnter}
                    on:click={() => toggle()}
                    class="navigatorButton"
                >
                    <Icon icon="table-of-contents" />
                </div>
                <div
                    tabindex={$audioButtonVisible ? 0 : -1}
                    id="player-icon"
                    on:click={() => ($openAudio = !$openAudio)}
                    aria-label="open music"
                    role="button"
                    aria-hidden={!$audioButtonVisible}
                    on:keydown={clickEnter}
                    class="relative w-4 ml-3 flex items-center transition-all h-full aria-hidden:w-[1px] aria-hidden:opacity-0 aria-hidden:pointer-events-none"
                >
                    <Icon
                        icon={$checking || ($audioItem && $buffering) ? "loader" : "speaker"}
                        class="w-full absolute"
                    />
                </div>
            </div>
            <!-- svelte-ignore a11y-click-events-have-key-events -->
            <div
                class="topBarHeaderContainer"
                on:click={() => window.dispatchEvent(new CustomEvent("toptap"))}
            >
                <span class="topBarHeading">
                    {#if _mode == "beta"}
                        <img
                            alt="beta testing"
                            style="position:absolute;left: -3rem;width: 3rem;opacity: 0.6"
                            src="/images/beta.png"
                        />
                    {/if}
                    {_mode == "development"
                        ? "ACIM CE Dev"
                        : _mode == "beta"
                          ? "A Course in Miracles"
                          : "A Course in Miracles"}
                </span>
                <span class="topBarSubheading">Complete & Annotated Edition</span>
            </div>
            <div class="flex items-center" style:justify-content="flex-end">
                <div
                    tabindex={tabsButtonVisible ? 0 : -1}
                    role="button"
                    id="toggle-tabs"
                    aria-label={amountOfTabs ? "toggle top navigator" : "new tab"}
                    class:hidden={!tabsButtonVisible}
                    aria-hidden={!tabsButtonVisible}
                    on:keydown={clickEnter}
                    on:click={openPages}
                    class="pagesIcon"
                    class:open={tabsOpen && amountOfTabs}
                    class:mode-add={!amountOfTabs}
                >
                    <Icon icon={amountOfTabs ? "compass" : "new-tab"} />
                </div>
                <div
                    tabindex="0"
                    role="button"
                    class="threeDots"
                    aria-label="open side menu"
                    on:keydown={clickEnter}
                    on:click={() => ($sideMenuOpen = true)}
                >
                    <Icon icon="meatballs" class="w-5 h-5" />
                </div>
            </div>
        </div>
        <TopNavigation />
    </header>
{/if}

<style>
    .topBarHeaderContainer {
        display: flex;
        align-items: center;
        flex-direction: column;
    }

    .topBarSubheading {
        font-size: 12px;
    }

    .topBarHeading {
        position: relative;
    }

    .pagesIcon {
        width: 24px;
        height: 24px;
        margin-right: 10px;
        position: relative;
        transition:
            transform 0.4s ease-in-out,
            width 0.4s ease-in-out,
            height 0.4s ease-in-out,
            opacity 0.2s linear;
    }
    .pagesIcon.mode-add {
        transition: none;
    }

    .pagesIcon.hidden {
        width: 0;
        height: 0;
        opacity: 0;
    }

    .pagesIcon.open {
        transform: rotate(45deg);
    }

    .pagesIcon :global(.compass-inside) {
        transition: transform 0.4s ease-in-out;
        transform: rotate(0deg);
        transform-origin: center;
    }

    .pagesIcon.open :global(.compass-inside) {
        transform: rotate(135deg);
    }
</style>
